import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Center, Flex, StyleProps, Text, XOutline } from '@bonusly/design-system';

import { User } from '../../../schemas/users';
import { InlineSelector } from './inline-selector';

type UserSelectorProps = StyleProps & {
	onChange: (users: User[]) => void;
	onSearch: (query: string) => Promise<User[]>;
	onBlur?: () => void;
	limit?: number;
	value: User[];
	placeholder?: string;
};

export const UserUsernameSelector: FC<UserSelectorProps> = ({ onChange, onSearch, onBlur, value, placeholder, ...rest }) => {
	const { t } = useTranslation('components', { keyPrefix: 'user_selector' });

	const handleSearch = async (query: string) => {
		try {
			const results = await onSearch(query);
			// Filter out already selected users and excluded users
			return results.filter((user) => !value.some((selected) => selected.id === user.id));
		} catch (error) {
			console.error('Error searching users:', error);
			return [];
		}
	};

	return (
		<InlineSelector
			aria-label={placeholder || t('placeholder')}
			emptyState={
				<Center py={4}>
					<Text color="text.softest">{t('empty_state')}</Text>
				</Center>
			}
			onRemove={(user) => onChange(value.filter((u) => u.id !== user.id))}
			onSearch={handleSearch}
			onBlur={onBlur}
			onSelect={(user) => {
				// Add the new user to the existing selection
				if (!value.some((selected) => selected.id === user.id)) {
					onChange([...value, user]);
				}
			}}
			placeholder={placeholder || t('placeholder')}
			value={value}
			limit={rest.limit || 50}
			renderSelectedItem={({ isSelected, onRemove, value: user }) => (
				<Flex
					alignItems="center"
					aria-label={t('selected_label', { name: user.username })}
					as="span"
					bgColor="primary.bgAlpha"
					borderRadius="full"
					color="primary.fgStrong"
					display="inline-flex"
					gap={1}
					px={3}
					py={1}
					_selected={{
						outline: '2px solid',
						outlineColor: 'border.focus',
					}}
					{...(isSelected && {
						'data-selected': true,
					})}
				>
					@{user.username}
					<Center
						as="button"
						type="button"
						onMouseDown={(e: MouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							onRemove(e);
						}}
					>
						<XOutline size="xs" />
					</Center>
				</Flex>
			)}
			renderMenuItem={({ isSelected, option: user, onSelect }) => (
				<Box
					as="button"
					type="button"
					aria-label={t('select_user_label', { name: user.displayName }) || `Select ${user.displayName}`}
					borderRadius="sm"
					cursor="pointer"
					display="block"
					onMouseDown={(e: MouseEvent) => {
						e.preventDefault();
						onSelect(e);
					}}
					p={0}
					transition="background-color 0.2s ease-in-out"
					w="full"
					_hover={{
						bgColor: 'primary.bgAlpha',
					}}
					_selected={{
						bgColor: 'primary.bgAlpha',
					}}
					{...(isSelected && {
						'data-selected': true,
					})}
				>
					<Flex alignItems="center" px={2} py={1}>
						<Avatar name={user.displayName} mr={2} size="sm" src={user.profilePic} />

						<Box flex={1} minWidth={0} textAlign="left">
							<Text fontSize="sm" fontWeight="medium" lineHeight={5} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
								{user.displayName}
							</Text>

							<Text color="text.softer" fontSize="xs" lineHeight={4} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
								@{user.username}
							</Text>
						</Box>
					</Flex>
				</Box>
			)}
			{...rest}
		/>
	);
};
